<template>
  <div class="mssp-kw">
    <div v-if="loading" class="mssp-kw__loading">
      <v-progress-circular indeterminate color="primary" size="24" />
    </div>

    <template v-else-if="orgSummary.length">
      <!-- Summary + Search row -->
      <div class="mssp-kw__toolbar">
        <div class="mssp-kw__pills">
          <span class="pill">
            <strong>{{ totalKeywords }}</strong> Total Keywords
          </span>
          <span class="pill">
            <strong>{{ licensedCount }}</strong> Orgs Licensed
          </span>
          <span v-if="cappedCount" class="pill pill--warn">
            <strong>{{ cappedCount }}</strong> Orgs Capped
          </span>
        </div>
        <div class="mssp-kw__search">
          <v-icon small color="grey">search</v-icon>
          <input
            v-model="searchQuery"
            type="text"
            class="mssp-kw__search-input"
            placeholder="Search organizations..."
          >
        </div>
      </div>

      <!-- Org cards grid -->
      <div class="org-grid">
        <div
          v-for="org in paginatedOrgs"
          :key="org.orgId"
          class="org-card"
          :class="{
            'org-card--capped': isAtCap(org),
            'org-card--off': !org.hasInfostealers,
            'org-card--default': org.isDefault,
          }"
        >
          <!-- Card header -->
          <div class="org-card__head">
            <span class="org-card__name">
              {{ org.orgName }}
              <span
                v-if="org.isDefault"
                class="org-card__tag"
              >Default</span>
            </span>
            <span v-if="org.hasInfostealers" class="org-card__cap">
              <span :class="cntClass(org)">{{ org.count }}</span>
              / {{ org.cap != null ? org.cap : '∞' }}
            </span>
            <span v-else-if="org.count" class="org-card__cap">
              <span class="org-card__cnt--orange">{{ org.count }}</span>
              <span class="org-card__nolic-sm">no license</span>
            </span>
          </div>

          <!-- Usage bar -->
          <div
            v-if="org.hasInfostealers && org.cap"
            class="org-card__bar"
          >
            <div
              class="org-card__fill"
              :class="barClass(org)"
              :style="{ width: Math.max(usagePct(org), 2) + '%' }"
            />
          </div>

          <!-- Per-source breakdown (only when api keywords ride on this org) -->
          <div v-if="org.apiCount" class="org-card__legend">
            <span class="org-card__legend-item">
              <span class="org-card__dot org-card__dot--platform" />
              Cynergy Platform <b>{{ org.platformCount }}</b>
            </span>
            <span class="org-card__legend-item">
              <span class="org-card__dot org-card__dot--api" />
              Cynergy API <b>{{ org.apiCount }}</b>
            </span>
          </div>

          <!-- Keywords + Add button -->
          <div class="org-card__body">
            <div class="org-card__kws">
              <v-chip
                v-for="kw in org.keywords"
                :key="kw"
                label close x-small
                text-color="#2459d5"
                class="org-card__chip"
                close-icon="close"
                @click:close="removeKw(org, kw)"
              >{{ kw }}</v-chip>
              <!-- api keywords: read-only (managed via the API) -->
              <v-chip
                v-for="kw in org.apiKeywords"
                :key="`api-${kw}`"
                label x-small
                text-color="#2459d5"
                class="org-card__chip org-card__chip--api"
                title="Added via the API — manage it from the API Hub"
              >{{ kw }}<span class="org-card__chip-badge">API</span></v-chip>
              <span
                v-if="!org.keywords.length && !org.apiCount && org.hasInfostealers"
                class="org-card__empty"
              >No keywords</span>
              <div
                v-if="!org.hasInfostealers && !org.count"
                class="org-card__nolic"
              >
                <v-icon x-small color="grey">lock</v-icon>
                No infostealers license
              </div>
            </div>
            <div class="org-card__footer">
              <v-btn
                x-small
                outlined
                color="primary"
                class="org-card__add"
                :disabled="!org.hasInfostealers || isAtCap(org)"
                @click="openAdd(org)"
              >
                <v-icon x-small left>add</v-icon> Add
              </v-btn>
              <span
                v-if="isAtCap(org)"
                class="org-card__cap-msg"
              >
                <v-icon x-small color="error">block</v-icon>
                Limit reached
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- No results -->
      <div
        v-if="!paginatedOrgs.length && searchQuery"
        class="mssp-kw__no-match"
      >
        <v-icon small color="grey">search_off</v-icon>
        No match for "{{ searchQuery }}"
      </div>

      <!-- Pagination -->
      <div v-if="pages > 1" class="mssp-kw__pager">
        <button
          class="mssp-kw__pg-btn"
          :disabled="page <= 1"
          @click="page--"
        ><v-icon small>chevron_left</v-icon></button>
        <span class="mssp-kw__pg-info">
          {{ page }} / {{ pages }}
        </span>
        <button
          class="mssp-kw__pg-btn"
          :disabled="page >= pages"
          @click="page++"
        ><v-icon small>chevron_right</v-icon></button>
      </div>
    </template>

    <div v-else class="mssp-kw__empty">
      <v-icon color="grey">info</v-icon>
      No organizations found
    </div>

    <!-- Add keyword dialog -->
    <v-dialog
      :value="showAdd"
      max-width="420"
      @click:outside="showAdd = false"
      @keydown.esc="showAdd = false"
    >
      <v-card class="mssp-kw__dlg">
        <v-card-title class="mssp-kw__dlg-title">
          Add Keyword — {{ addOrg ? addOrg.orgName : '' }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newKw"
            label="Keyword"
            outlined dense autofocus
            :error-messages="addErr"
            @keydown.enter="confirmAdd"
          />
          <div v-if="addOrg" class="mssp-kw__dlg-cap">
            {{ addOrg.count }} / {{ addOrg.cap || '∞' }} used
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showAdd = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!newKw.trim() || saving"
            :loading="saving"
            @click="confirmAdd"
          >Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ADMIN_SYSTEM_SETTINGS } from '@/store/user/actionTypes';

const PER_PAGE = 6;

function normalizePlatformKeywords(bucket, defaultOrgId) {
  return (bucket || []).map(entry => (typeof entry === 'string'
    ? { orgId: defaultOrgId || 'tenant', keyword: entry }
    : entry));
}

export default {
  name: 'MsspBrandProtection',
  data() {
    return {
      searchQuery: '',
      page: 1,
      showAdd: false,
      addOrg: null,
      newKw: '',
      addErr: '',
      saving: false,
    };
  },
  watch: { searchQuery() { this.page = 1; } },
  computed: {
    ...mapGetters(['systemSettings', 'licenseData']),
    loading() { return !this.licenseData; },
    defaultOrgId() {
      const orgs = this.licenseData?.organizations || [];
      // Default org is isCustomer but NOT a supply chain org
      const def = orgs.find(
        o => o.isCustomer && !o.isSupplyChain,
      );
      return def?.id || null;
    },
    platformBucket() {
      const bp = this.systemSettings?.settingsMetaData
        ?.brandProtection;
      const kbs = bp?.keywordsBySource?.platform;
      return (kbs && kbs.length > 0) ? kbs : (bp?.keywordsList || []);
    },
    normalized() {
      return normalizePlatformKeywords(
        this.platformBucket, this.defaultOrgId,
      );
    },
    // Tenant-wide api-hub keywords (deduped). They have no org, so they are
    // attributed to the default (customer) org below. cyn-ai is never included.
    apiKeywords() {
      const bp = this.systemSettings?.settingsMetaData?.brandProtection;
      const api = bp?.keywordsBySource?.api || [];
      const seen = new Set();
      const out = [];
      api.forEach(e => {
        const kw = typeof e === 'string' ? e : e.keyword;
        if (!kw || seen.has(kw)) return;
        seen.add(kw);
        out.push(kw);
      });
      return out;
    },
    orgSummary() {
      const orgs = this.licenseData?.organizations || [];
      return orgs.map(org => {
        const kws = this.normalized
          .filter(e => e.orgId === org.id)
          .map(e => e.keyword);
        const isDefault = org.id === this.defaultOrgId;
        // api keywords ride on the default org only, read-only, and share its cap.
        const apiKws = isDefault
          ? this.apiKeywords.filter(k => !kws.includes(k))
          : [];
        // Resolve infostealers cap from org licenses
        let cap = null;
        let hasInfostealers = false;
        (org.licenses || []).forEach(lic => {
          if (lic.isExpired) return;
          const d = lic.skuDetails;
          if (d?.module === 'infostealers' && d?.keywords != null) {
            cap = cap != null ? Math.max(cap, d.keywords) : d.keywords;
            hasInfostealers = true;
          }
        });
        return {
          orgId: org.id,
          orgName: org.name,
          isDefault,
          keywords: kws,
          apiKeywords: apiKws,
          platformCount: kws.length,
          apiCount: apiKws.length,
          count: kws.length + apiKws.length,
          cap,
          hasInfostealers,
        };
      });
    },
    totalKeywords() {
      return this.orgSummary.reduce((s, o) => s + o.count, 0);
    },
    licensedCount() {
      return this.orgSummary.filter(o => o.hasInfostealers).length;
    },
    cappedCount() {
      return this.orgSummary.filter(o => this.isAtCap(o)).length;
    },
    filtered() {
      const q = this.searchQuery.trim().toLowerCase();
      let list = this.orgSummary;
      if (q) {
        list = list.filter(
          o => o.orgName.toLowerCase().includes(q),
        );
      }
      return list.slice().sort((a, b) => {
        // Default org always first
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        // Licensed before unlicensed
        if (a.hasInfostealers && !b.hasInfostealers) return -1;
        if (!a.hasInfostealers && b.hasInfostealers) return 1;
        // Alphabetical within each group
        return a.orgName.localeCompare(b.orgName);
      });
    },
    pages() {
      return Math.ceil(this.filtered.length / PER_PAGE) || 1;
    },
    paginatedOrgs() {
      const s = (this.page - 1) * PER_PAGE;
      return this.filtered.slice(s, s + PER_PAGE);
    },
  },
  methods: {
    usagePct(o) {
      if (!o.cap) return 0;
      return Math.min(
        100, Math.round((o.count / o.cap) * 100),
      );
    },
    isAtCap(o) {
      return o.cap != null && o.count >= o.cap;
    },
    cntClass(o) {
      const p = this.usagePct(o);
      if (p >= 100) return 'org-card__cnt--red';
      if (p >= 75) return 'org-card__cnt--orange';
      return '';
    },
    barClass(o) {
      const p = this.usagePct(o);
      if (p >= 100) return 'org-card__fill--red';
      if (p >= 75) return 'org-card__fill--orange';
      return 'org-card__fill--green';
    },
    openAdd(org) {
      this.addOrg = org;
      this.newKw = '';
      this.addErr = '';
      this.showAdd = true;
    },
    async confirmAdd() {
      const kw = this.newKw.trim().toLowerCase();
      if (!kw) return;
      if (this.addOrg.keywords.includes(kw)) {
        this.addErr = 'Already exists for this org';
        return;
      }
      this.saving = true;
      this.addErr = '';
      try {
        const updated = [...this.addOrg.keywords, kw];
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, {
          brandProtection: {
            ...this.systemSettings?.settingsMetaData
              ?.brandProtection,
            keywordsList: updated,
            orgId: this.addOrg.orgId,
            defaultOrgId: this.defaultOrgId,
          },
        });
        this.showAdd = false;
      } catch (e) {
        this.addErr = e?.message || 'Failed';
      } finally {
        this.saving = false;
      }
    },
    async removeKw(org, keyword) {
      try {
        const updated = org.keywords.filter(k => k !== keyword);
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, {
          brandProtection: {
            ...this.systemSettings?.settingsMetaData
              ?.brandProtection,
            keywordsList: updated,
            orgId: org.orgId,
            defaultOrgId: this.defaultOrgId,
          },
        });
      } catch (e) {
        /* parent handles */
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_sizing";
@import "@/assets/styles/_colors";
@import "@/assets/styles/_semantic-sizing";

.mssp-kw {
  &__loading {
    display: flex;
    justify-content: center;
    padding: $space-10 0;
  }
  &__empty, &__no-match {
    display: flex;
    align-items: center;
    gap: $space-2;
    padding: $space-6;
    color: var(--text-secondary);
    font-size: $font-size-sm;
  }
  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-2;
    margin-bottom: $space-2;
    flex-wrap: wrap;
  }
  &__pills {
    display: flex;
    gap: $space-2;
  }
  &__search {
    display: flex;
    align-items: center;
    gap: $space-1-5;
    padding: $space-1 $space-2-5;
    border: $border-width-thin solid var(--border-primary);
    border-radius: $radius-full;
    background: var(--bg-primary);
    min-width: 180px;
    &:focus-within { border-color: var(--brand-primary); }
  }
  &__search-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    color: var(--text-primary);
    &::placeholder {
      color: var(--text-secondary);
      opacity: 0.6;
    }
  }
  &__pager {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $space-3;
    margin-top: $space-4;
  }
  &__pg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: $border-width-thin solid var(--border-primary);
    border-radius: $radius-6;
    background: var(--bg-primary);
    cursor: pointer;
    color: var(--text-primary);
    &:hover:not(:disabled) {
      background: var(--bg-tertiary, $gray-100);
    }
    &:disabled { opacity: 0.35; cursor: not-allowed; }
  }
  &__pg-info {
    font-size: $font-size-sm;
    color: var(--text-secondary);
  }
  &__dlg { border-radius: $radius-lg; }
  &__dlg-title {
    font-family: $font-family-bold;
    font-size: $font-size-base;
  }
  &__dlg-cap {
    font-size: $font-size-sm;
    color: var(--text-secondary);
    margin-top: $space-1;
  }
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: $space-1;
  padding: $space-1-5 $space-3;
  border-radius: $radius-full;
  font-family: $font-family-regular;
  font-size: $font-size-sm;
  color: var(--text-primary);
  background: rgba(99, 102, 241, 0.06);
  border: 1px solid rgba(99, 102, 241, 0.15);
  strong {
    font-family: $font-family-bold;
    color: var(--brand-primary, #6366f1);
  }
  &--warn {
    background: rgba($error-500, 0.06);
    border-color: rgba($error-500, 0.25);
    strong { color: $error-500; }
  }
}

.org-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $space-2-5;
}

.org-card {
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-lg;
  padding: $space-2-5 $space-3;
  background: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  gap: $space-1-5;
  transition: border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: var(--brand-primary, #6366f1);
    box-shadow: 0 2px 12px rgba(99, 102, 241, 0.1);
  }

  &--capped {
    border-color: rgba($error-500, 0.4);
    background: rgba($error-500, 0.03);
  }
  &--off {
    opacity: 0.5;
    .org-card__chip {
      opacity: 0.7;
    }
    .org-card__add {
      opacity: 0.5;
    }
  }
  &--default {
    border-color: rgba(99, 102, 241, 0.3);
    background: rgba(99, 102, 241, 0.02);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-2;
  }
  &__name {
    font-family: $font-family-bold;
    font-size: $font-size-base;
    color: var(--text-primary);
    display: flex;
    align-items: center;
    gap: $space-1-5;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__tag {
    font-size: 10px;
    padding: 2px $space-2;
    border-radius: $radius-full;
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
    font-family: $font-family-bold;
    flex-shrink: 0;
  }
  &__cap {
    font-family: $font-family-bold;
    font-size: $font-size-base;
    font-variant-numeric: tabular-nums;
    color: var(--text-primary);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: $space-1;
  }
  &__cnt--orange { color: #f97316; }
  &__cnt--red { color: $error-500; }
  &__nolic-sm {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    font-family: $font-family-regular;
  }

  &__bar {
    height: 6px;
    background: var(--bg-tertiary, $gray-200);
    border-radius: $radius-full;
    overflow: hidden;
  }
  &__fill {
    height: 100%;
    border-radius: $radius-full;
    transition: width 0.4s ease;
    min-width: 2px;
    &--green {
      background: linear-gradient(
        90deg, $success-500, lighten($success-500, 8%)
      );
    }
    &--orange {
      background: linear-gradient(90deg, #f97316, #fb923c);
    }
    &--red {
      background: linear-gradient(
        90deg, $error-500, lighten($error-500, 8%)
      );
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    min-height: 70px;
    flex: 1;
  }
  &__kws {
    display: flex;
    flex-wrap: wrap;
    gap: $space-1;
    flex: 1;
  }
  &__chip {
    background-color: rgba(99, 102, 241, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.18);
    font-size: 11px;
    &.v-chip--label { border-radius: 6px; }
  }
  &__chip--api {
    background-color: rgba(148, 163, 184, 0.12);
    border: 1px solid rgba(148, 163, 184, 0.30);
  }
  &__chip-badge {
    margin-left: 4px;
    padding: 0 3px;
    font-size: 9px;
    font-family: $font-family-bold;
    color: var(--text-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  &__legend {
    display: flex;
    gap: $space-3;
    margin: $space-1 0 $space-2;
    font-size: 11px;
    color: var(--text-secondary);
  }
  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    b { color: var(--text-primary); font-family: $font-family-bold; }
  }
  &__dot {
    width: 7px;
    height: 7px;
    border-radius: $radius-full;
    &--platform { background: #6366f1; }
    &--api { background: #94a3b8; }
  }
  &__empty {
    color: var(--text-secondary);
    font-size: $font-size-xs;
    font-style: italic;
    padding: $space-1 0;
  }
  &__add {
    align-self: flex-start;
    text-transform: none;
    letter-spacing: normal;
    font-family: $font-family-bold;
  }
  &__cap-msg {
    display: flex;
    align-items: center;
    gap: $space-1;
    font-size: $font-size-xs;
    color: $error-500;
    font-family: $font-family-bold;
  }
  &__nolic {
    display: flex;
    align-items: center;
    gap: $space-1-5;
    color: var(--text-secondary);
    font-size: $font-size-xs;
    font-style: italic;
    padding: $space-1-5 $space-2;
    background: var(--bg-tertiary, $gray-100);
    border-radius: $radius-md;
    width: fit-content;
  }
}
</style>
